import Rating from '@/entities/Rating/ui/Rating';
import { DropzoneFiles } from '@/features/DropzoneFiles/ui/DropzoneFiles';
import { usePopupsStore } from '@/shared/store/popupsStore';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input/Input';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock-upgrade';
import { Form, Formik } from 'formik';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useEffect, useRef } from 'react';
import {
  cvaBody,
  cvaButton,
  cvaClose,
  cvaCloseIcon,
  cvaDescr,
  cvaFormNote,
  cvaGroup,
  cvaGroupTitle,
  cvaHeader,
  cvaInner,
  cvaItem,
  cvaItemImage,
  cvaItemImageWrap,
  cvaItemTitle,
  cvaOverlay,
  cvaRoot,
  cvaTerms,
  cvaTitle,
} from './ReviewFormPopupStyles';
import IconClose from '/public/icons/close.svg';

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

const formInitialValues = {
  review: '',
};

const ReviewFormPopup: FC<Props> = ({ onClose, onSuccess }) => {
  const innerRef = useRef<HTMLDivElement>(null);

  const setShowCallbackPopup = usePopupsStore((state) => state.setShowCallbackPopup);

  const handleSubmit = async () => {
    try {
      // todo
    } catch (e) {
      // console.error(e);
    }
  };

  const handleClickCallback = (e) => {
    e.preventDefault();
    onClose();
    setShowCallbackPopup(true);
  };

  useEffect(() => {
    if (innerRef.current) {
      disableBodyScroll(innerRef.current, {
        reserveScrollBarGap: true,
      });
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeInOut',
      }}
      className={cvaRoot()}
      data-lenis-prevent
    >
      <div className={cvaOverlay()} onClick={() => onClose()}></div>
      <button type="button" className={cvaClose()} onClick={() => onClose()}>
        <IconClose className={cvaCloseIcon()} />
      </button>
      <div className={cvaInner()} ref={innerRef}>
        <Formik initialValues={formInitialValues} enableReinitialize onSubmit={handleSubmit}>
          {({}) => (
            <Form autoComplete="off">
              <div className={cvaHeader()}>
                <h2 className={cvaTitle()}>
                  Оставить отзыв <br />о товаре
                </h2>
                <div className={cvaDescr()}>Ваш отзыв поможет кому-то сделать выбор. Спасибо, что делитесь опытом!</div>
                <div className={cvaItem()}>
                  <div className={cvaItemImageWrap()}>
                    <Image className={cvaItemImage()} src={'/images/review_form_item.png'} fill alt="" />
                  </div>
                  <div className={cvaItemTitle()}>SOS-корректор с экстрактом чайного дерева для проблемной кожи</div>
                </div>
              </div>
              <div className={cvaBody()}>
                <div className={cvaGroup()}>
                  <h3
                    className={cvaGroupTitle({
                      marginBottomSize: 'small',
                    })}
                  >
                    Ваша оценка товара
                  </h3>
                  <Rating value={5} selectable />
                </div>
                <div className={cvaGroup()}>
                  <h3
                    className={cvaGroupTitle({
                      marginBottomSize: 'large',
                    })}
                  >
                    Ваши впечатления о товаре
                  </h3>
                  <Input as={'textarea'} name={'review'} placeholder={'Отзыв'} required />
                  <div className={cvaFormNote()}>
                    Если у вас вопрос или жалоба, напишите нам в форму{' '}
                    <Link href={'/'} onClick={handleClickCallback}>
                      обратной связи
                    </Link>
                  </div>
                </div>
                <div className={cvaGroup()}>
                  <h3
                    className={cvaGroupTitle({
                      marginBottomSize: 'large',
                    })}
                  >
                    Фото «До» применения
                  </h3>
                  <DropzoneFiles
                    placeholder={'Нажмите на кнопку или&nbsp;перетащите фото в эту область'}
                    multiple
                    onChange={() => {}}
                    accept={{
                      'image/png': ['.png'],
                      'image/jpeg': ['.jpg', '.jpeg'],
                    }}
                  />
                </div>
                <div className={cvaGroup()}>
                  <h3
                    className={cvaGroupTitle({
                      marginBottomSize: 'large',
                    })}
                  >
                    Фото «После» применения
                  </h3>
                  <DropzoneFiles
                    placeholder={'Нажмите на кнопку или&nbsp;перетащите фото в эту область'}
                    multiple
                    onChange={() => {}}
                    accept={{
                      'image/png': ['.png'],
                      'image/jpeg': ['.jpg', '.jpeg'],
                    }}
                  />
                </div>
                <div className={cvaGroup()}>
                  <h3
                    className={cvaGroupTitle({
                      marginBottomSize: 'large',
                    })}
                  >
                    Видео
                  </h3>
                  <DropzoneFiles
                    placeholder={'Нажмите на кнопку или&nbsp;перетащите фото в эту область'}
                    multiple
                    onChange={() => {}}
                    accept={{
                      'video/*': ['.mp4'],
                    }}
                  />
                </div>
                <div className={cvaTerms()}>
                  Нажимая кнопку «Отправить», я соглашаюсь c&nbsp;
                  <Link href={process.env.NEXT_PUBLIC_FRONT_BASE_URL + '/upload/politics.pdf'} prefetch={false}>
                    политикой конфиденциальности
                  </Link>{' '}
                  и&nbsp;
                  <Link href={process.env.NEXT_PUBLIC_FRONT_BASE_URL + '/upload/soglasie_upd.pdf'} prefetch={false}>
                    пользовательским соглашением
                  </Link>
                </div>
                <Button
                  className={cvaButton()}
                  color={'mediumGreen'}
                  hoverEffect={'highlight'}
                  size={'medium'}
                  onClick={() => onSuccess?.()}
                >
                  Отправить
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </motion.div>
  );
};

export default ReviewFormPopup;
